import React, { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Grid,
  MenuItem,
  Typography,
  CircularProgress,
  Box,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import LeaveViewController from "../../redux/actions/leaveApiAction";
import EmployeeProfileVeiwController from "../../redux/actions/EmployeeProfileApiAction";
import FormDrawer from "../../components/layout/FormDrawer";
import { BRAND, primaryButtonSx, inputSx, mobileInputSx } from "../../components/auth/authTheme";

import "../../styles/styles.css";
import "../../styles/layout.css";

const validationSchema = Yup.object({
  employee: Yup.string().required("Employee is required"),
  leave_year: Yup.number()
    .typeError("Leave year is required")
    .required("Leave year is required")
    .min(2000, "Enter a valid year")
    .max(2100, "Enter a valid year"),
  casual_leave_balance: Yup.number()
    .typeError("Enter a valid number")
    .min(0, "Cannot be negative")
    .required("Required"),
  sick_leave_balance: Yup.number()
    .typeError("Enter a valid number")
    .min(0, "Cannot be negative")
    .required("Required"),
  maternity_leave_balance: Yup.number()
    .typeError("Enter a valid number")
    .min(0, "Cannot be negative")
    .required("Required"),
  marriage_leave_balance: Yup.number()
    .typeError("Enter a valid number")
    .min(0, "Cannot be negative")
    .required("Required"),
  compensatory_off_leave_balance: Yup.number()
    .typeError("Enter a valid number")
    .min(0, "Cannot be negative")
    .required("Required"),
  unpaid_leave_balance: Yup.number()
    .typeError("Enter a valid number")
    .min(0, "Cannot be negative")
    .required("Required"),
  carry_forward_days: Yup.number()
    .typeError("Enter a valid number")
    .min(0, "Cannot be negative")
    .required("Required"),
});

const emptyValues = {
  employee: "",
  leave_year: new Date().getFullYear(),
  casual_leave_balance: 0,
  sick_leave_balance: 0,
  maternity_leave_balance: 0,
  marriage_leave_balance: 0,
  compensatory_off_leave_balance: 0,
  unpaid_leave_balance: 0,
  carry_forward_days: 0,
};

const balanceFields = [
  { name: "casual_leave_balance", label: "Casual Leave" },
  { name: "sick_leave_balance", label: "Sick Leave" },
  { name: "maternity_leave_balance", label: "Maternity Leave" },
  { name: "marriage_leave_balance", label: "Marriage Leave" },
  { name: "compensatory_off_leave_balance", label: "Compensatory Off" },
  { name: "unpaid_leave_balance", label: "Unpaid Leave Balance" },
  { name: "carry_forward_days", label: "Carry Forward Leave" },
];

function AddLeaveBalanceFormModal({ show, handleClose, initialData = null }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fieldSx = isMobile ? mobileInputSx : inputSx;
  const { getEmployees } = EmployeeProfileVeiwController();
  const { addLeaveBalance, updateLeaveBalance } = LeaveViewController();
  const { employee_data } = useSelector((state) => state.employeeProfile);
  const { isLoding } = useSelector((state) => state.leave);

  const isEdit = Boolean(initialData?.id);
  const [initialValues, setInitialValues] = useState(emptyValues);

  useEffect(() => {
    if (show) {
      dispatch(getEmployees());
    }
  }, [show, dispatch]);

  useEffect(() => {
    if (!show) return;

    if (initialData) {
      setInitialValues({
        employee: initialData.employee?.id || "",
        leave_year: initialData.leave_year || new Date().getFullYear(),
        casual_leave_balance: initialData.casual_leave_balance ?? 0,
        sick_leave_balance: initialData.sick_leave_balance ?? 0,
        maternity_leave_balance: initialData.maternity_leave_balance ?? 0,
        marriage_leave_balance: initialData.marriage_leave_balance ?? 0,
        compensatory_off_leave_balance:
          initialData.compensatory_off_leave_balance ?? 0,
        unpaid_leave_balance: initialData.unpaid_leave_balance ?? 0,
        carry_forward_days: initialData.carry_forward_days ?? 0,
      });
    } else {
      setInitialValues({ ...emptyValues, leave_year: new Date().getFullYear() });
    }
  }, [show, initialData]);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const action = isEdit
        ? updateLeaveBalance(initialData.id, values)
        : addLeaveBalance(values);
      await dispatch(action);
      resetForm();
      handleClose();
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const employees = employee_data?.employees || [];

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnBlur
      validateOnChange={false}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting,
        submitForm,
      }) => {
        const isSaving = isSubmitting || isLoding;

        return (
          <Form style={{ display: "contents" }}>
            <FormDrawer
              open={show}
              onClose={handleClose}
              title={isEdit ? "Edit Leave Balance" : "Manage Leave Balance"}
              subtitle={
                isEdit
                  ? "Update allocated leave balances for the employee"
                  : "Set leave balances for an employee for the selected year"
              }
              width={{ xs: "100%", sm: 520, md: 560 }}
              footer={
                isMobile ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      disabled={isSaving}
                      onClick={() => submitForm()}
                      fullWidth
                      sx={{
                        ...primaryButtonSx,
                        minHeight: 48,
                        borderRadius: "12px",
                        "&.Mui-disabled": {
                          bgcolor: isSaving ? BRAND.orange : undefined,
                          color: isSaving ? "#fff" : undefined,
                          opacity: isSaving ? 0.85 : undefined,
                        },
                      }}
                      startIcon={
                        isSaving ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : null
                      }
                    >
                      {isSaving
                        ? isEdit
                          ? "Updating..."
                          : "Saving..."
                        : isEdit
                          ? "Update"
                          : "Submit"}
                    </Button>
                    <Button
                      onClick={handleClose}
                      color="inherit"
                      disabled={isSaving}
                      fullWidth
                      sx={{ minHeight: 44, textTransform: "none", fontWeight: 600 }}
                    >
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Button
                      onClick={handleClose}
                      color="inherit"
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      disabled={isSaving}
                      onClick={() => submitForm()}
                      sx={{
                        ...primaryButtonSx,
                        minWidth: 120,
                        "&.Mui-disabled": {
                          bgcolor: isSaving ? BRAND.orange : undefined,
                          color: isSaving ? "#fff" : undefined,
                          opacity: isSaving ? 0.85 : undefined,
                        },
                      }}
                      startIcon={
                        isSaving ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : null
                      }
                    >
                      {isSaving
                        ? isEdit
                          ? "Updating..."
                          : "Saving..."
                        : isEdit
                          ? "Update"
                          : "Submit"}
                    </Button>
                  </>
                )
              }
            >
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: isMobile ? "12px" : 0,
                  border: isMobile ? `1px solid ${BRAND.border}` : "none",
                  p: isMobile ? 1.5 : 0,
                  mb: isMobile ? 1.5 : 3,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: BRAND.navy, fontWeight: 700, mb: 1.5 }}
                >
                  Employee details
                </Typography>

                <Grid container spacing={isMobile ? 1.5 : 2.5}>
                  <Grid item xs={12}>
                    <TextField
                      select
                      label="Employee"
                      name="employee"
                      value={values.employee}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      required
                      disabled={isEdit}
                      error={touched.employee && Boolean(errors.employee)}
                      helperText={touched.employee && errors.employee}
                      sx={fieldSx}
                    >
                      <MenuItem value="" disabled>
                        Select Employee
                      </MenuItem>
                      {employees.map((emp) => (
                        <MenuItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name}
                          {emp.employee_id ? ` (${emp.employee_id})` : ""}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Leave Year"
                      name="leave_year"
                      type="number"
                      value={values.leave_year}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      required
                      error={touched.leave_year && Boolean(errors.leave_year)}
                      helperText={touched.leave_year && errors.leave_year}
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: isMobile ? "12px" : 0,
                  border: isMobile ? `1px solid ${BRAND.border}` : "none",
                  p: isMobile ? 1.5 : 0,
                  mb: isMobile ? 1.5 : 0,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: BRAND.navy, fontWeight: 700, mb: 1.5 }}
                >
                  Leave balances
                </Typography>

                <Grid container spacing={isMobile ? 1.25 : 2.5}>
                  {balanceFields.map(({ name, label }) => (
                    <Grid item xs={6} sm={6} key={name}>
                      <TextField
                        label={label}
                        name={name}
                        type="number"
                        value={values[name]}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        inputProps={{ min: 0 }}
                        error={touched[name] && Boolean(errors[name])}
                        helperText={touched[name] && errors[name]}
                        sx={fieldSx}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Box
                sx={{
                  mt: isMobile ? 0 : 3,
                  p: isMobile ? 1.25 : 2,
                  borderRadius: "10px",
                  bgcolor: BRAND.bg,
                  border: `1px solid ${BRAND.border}`,
                }}
              >
                <Typography
                  sx={{
                    color: BRAND.muted,
                    fontSize: isMobile ? "0.8125rem" : "0.875rem",
                    lineHeight: 1.5,
                  }}
                >
                  Enter the total allocated days for each leave type. Remaining
                  balances are calculated automatically when leave is taken.
                </Typography>
              </Box>
            </FormDrawer>
          </Form>
        );
      }}
    </Formik>
  );
}

export default AddLeaveBalanceFormModal;
